import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

export interface AuthConfig {
  enabled: boolean;
  tenant_id?: string;
  client_id?: string;
  authority?: string;
  redirect_uri?: string;
}

export interface MsalInit {
  instance: PublicClientApplication | null;
  config: AuthConfig;
}

export async function initializeMsal(): Promise<MsalInit> {
  try {
    const resp = await fetch('/api/auth/config');

    if (!resp.ok) {
      console.warn('Auth config fetch failed:', resp.status, resp.statusText);
      return { instance: null, config: { enabled: false } };
    }

    const contentType = resp.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      console.warn('Auth config returned non-JSON response:', contentType);
      return { instance: null, config: { enabled: false } };
    }

    const config: AuthConfig = await resp.json();

    if (!config.enabled || !config.client_id || !config.authority) {
      return { instance: null, config: { enabled: false } };
    }

    const msalConfig: Configuration = {
      auth: {
        clientId: config.client_id,
        authority: config.authority,
        redirectUri: config.redirect_uri || window.location.origin,
        postLogoutRedirectUri: config.redirect_uri || window.location.origin,
      },
      cache: {
        cacheLocation: 'localStorage' as const,
      },
    };

    const instance = new PublicClientApplication(msalConfig);
    await instance.initialize();

    const redirectResult = await instance.handleRedirectPromise();
    if (redirectResult?.account) {
      instance.setActiveAccount(redirectResult.account);
    }

    return { instance, config };
  } catch (err) {
    console.error('MSAL initialization failed:', err);
    return { instance: null, config: { enabled: false } };
  }
}
